import React, { useState } from 'react';
import { ChevronRight, Users as UsersIcon, HelpCircle, Zap } from 'lucide-react';
import ScreenHeader from '../ScreenHeader';
import {
  CURSOS_DEMO, CURSO_NO_IDENTIFICADO, CONTEXTO, FRECUENCIA, INTENSIDAD,
  DURACION, PARTICIPANTES, FUENTE,
} from '../../data/observationEngine';

const ESTUDIANTE_NO_LO_SE = 'No lo sé';

/**
 * Variables rápidas (§11–§17). En modo "completo" todas son obligatorias
 * salvo "estudiante". En modo "rápido" sólo se pregunta contexto + intensidad
 * (curso opcional, el resto se llena con defaults seguros en MobileApp).
 */
const ScreenVariables = ({ dominio, onBack, onNext, draft, update, mode = 'completo' }) => {
  const [openCursos, setOpenCursos] = useState(false);
  const isQuick = mode === 'rapido';

  const set = (k, v) => update((d) => ({ ...d, [k]: v }));

  const canContinue = isQuick
    ? !!draft.contexto && !!draft.intensidad
    : !!draft.curso && !!draft.contexto && !!draft.frecuencia
      && !!draft.intensidad && !!draft.duracion && !!draft.participantes && !!draft.fuente;

  return (
    <div className="step-enter flex flex-col h-full">
      <ScreenHeader
        step={3}
        stepName="Contexto y variables"
        onBack={onBack}
        title="Contexto y variables rápidas"
        subtitle={isQuick
          ? 'Modo rápido: sólo lo esencial. Lo demás se completa con valores conservadores.'
          : 'Datos rápidos para que el equipo pueda actuar a tiempo.'}
        eta={isQuick ? '≈ 12s restantes' : '≈ 25s restantes'}
      />

      {isQuick && (
        <div className="px-5">
          <div className="rounded-xl bg-[#FFF9EC] border border-[#FFE7C2] px-3 py-2 flex items-center gap-2 text-xs text-brand-deep font-semibold" data-testid="var-quick-banner">
            <Zap className="w-4 h-4 text-[#C98718]" />
            Registro rápido activo
          </div>
        </div>
      )}

      <div className="flex-1 px-5 pt-4 overflow-y-auto pb-4 space-y-5">
        {/* Curso — opcional en quick */}
        <div>
          <span className="m-label">Curso o grupo {isQuick && <span className="text-brand-muted font-semibold">· opcional</span>}</span>
          <button
            data-testid="var-curso-toggle"
            onClick={() => setOpenCursos((v) => !v)}
            className="m-input flex items-center justify-between"
            type="button"
          >
            <span className={draft.curso ? 'text-brand-deep font-bold' : 'text-brand-muted'}>
              {draft.curso || 'Selecciona el curso'}
            </span>
            <ChevronRight className={`w-4 h-4 text-brand-muted transition-transform ${openCursos ? 'rotate-90' : ''}`} />
          </button>
          {openCursos && (
            <div data-testid="var-curso-grid" className="mt-2 rounded-2xl border border-[#DDE6F7] bg-white max-h-[220px] overflow-y-auto p-3">
              <button
                data-testid="var-curso-no-identificado"
                onClick={() => { set('curso', CURSO_NO_IDENTIFICADO); setOpenCursos(false); }}
                className={`w-full inline-flex items-center gap-2 text-[13px] font-bold rounded-xl px-3 py-2.5 border mb-2 ${
                  draft.curso === CURSO_NO_IDENTIFICADO
                    ? 'bg-brand-violet text-white border-brand-violet'
                    : 'bg-[#EEF4FF] text-brand-deep border-[#DDE6F7] hover:border-brand-violet'
                }`}
              >
                <HelpCircle className="w-4 h-4" />
                No identificado / No lo sé
              </button>

              <div className="grid grid-cols-4 gap-1.5">
                {CURSOS_DEMO.map((c) => (
                  <button
                    key={c}
                    data-testid={`var-curso-${c}`}
                    onClick={() => { set('curso', c); setOpenCursos(false); }}
                    className={`text-[12px] font-bold rounded-lg px-2 py-2 border ${
                      draft.curso === c
                        ? 'bg-brand-violet text-white border-brand-violet'
                        : 'bg-white text-brand-deep border-[#DDE6F7] hover:border-brand-violet'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Estudiante */}
        {!isQuick && (
          <div>
            <span className="m-label flex items-center gap-1.5">
              <UsersIcon className="w-3.5 h-3.5" />
              Estudiante involucrado · opcional
            </span>
            <input
              data-testid="var-estudiante"
              value={draft.estudiante || ''}
              onChange={(e) => set('estudiante', e.target.value)}
              placeholder="Nombre, iniciales o sin identificar"
              className="m-input"
            />
            <button
              data-testid="var-estudiante-no-lo-se"
              onClick={() => set('estudiante', ESTUDIANTE_NO_LO_SE)}
              className={`mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                draft.estudiante === ESTUDIANTE_NO_LO_SE
                  ? 'bg-brand-violet text-white border-brand-violet'
                  : 'bg-white text-brand-deep border-[#DDE6F7] hover:border-brand-violet'
              }`}
            >
              <HelpCircle className="w-3.5 h-3.5" /> No lo sé
            </button>
          </div>
        )}

        {/* Contexto — obligatorio en ambos modos */}
        <ChipBlock
          label="Contexto · ¿Dónde ocurrió?"
          name="contexto"
          options={CONTEXTO}
          value={draft.contexto}
          onChange={(v) => set('contexto', v)}
        />

        {/* Intensidad — obligatorio en ambos modos */}
        <ChipBlock
          label="Intensidad"
          name="intensidad"
          options={INTENSIDAD}
          value={draft.intensidad}
          onChange={(v) => set('intensidad', v)}
          withHint
        />

        {/* Resto — sólo modo completo */}
        {!isQuick && (
          <>
            <ChipBlock
              label="Frecuencia"
              name="frecuencia"
              options={FRECUENCIA}
              value={draft.frecuencia}
              onChange={(v) => set('frecuencia', v)}
              withHint
            />
            <ChipBlock
              label="Duración"
              name="duracion"
              options={DURACION}
              value={draft.duracion}
              onChange={(v) => set('duracion', v)}
            />
            <ChipBlock
              label="Participantes"
              name="participantes"
              options={PARTICIPANTES}
              value={draft.participantes}
              onChange={(v) => set('participantes', v)}
            />
            <ChipBlock
              label="Fuente de información"
              name="fuente"
              options={FUENTE}
              value={draft.fuente}
              onChange={(v) => set('fuente', v)}
            />
          </>
        )}
      </div>

      <div className="px-5 pb-6 pt-3 border-t border-[#F1F4FA] bg-white">
        <button data-testid="var-next" onClick={onNext} disabled={!canContinue} className="m-btn-primary">
          Continuar
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const ChipBlock = ({ label, name, options, value, onChange, withHint = false }) => (
  <div>
    <span className="m-label">{label}</span>
    <div className="flex flex-wrap gap-2" data-testid={`var-${name}`}>
      {options.map((o) => (
        <button
          key={o.v}
          data-testid={`var-${name}-${o.v}`}
          onClick={() => onChange(o.v)}
          className={`m-chip ${value === o.v ? 'is-active' : ''}`}
          title={withHint ? o.hint : undefined}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

export default ScreenVariables;
